import DOMPurify from "dompurify";
import { Timestamp } from "firebase/firestore";
import { Message } from "types";
import { toDate, TimestampLike } from "utils/dateUtils";
import { isPreAcceptance } from "utils/collabUtils";
import { waitingForVerification } from "utils/deliverableUtils";
import { Collab, Campaign, Client, MarketplaceOrderListItemDto, isSystemNote, activeLinks, activeUnits } from "@foodfluence/shared";

function collabIdsSet(collabData: Collab[]): Set<string> {
  return new Set(collabData.map((c) => c.id));
}

function marketplaceOrderIdsSet(marketplaceOrders: MarketplaceOrderListItemDto[]): Set<string> {
  return new Set(marketplaceOrders.map((o) => o.id));
}

function inboxConversationIdsSet(
  collabData: Collab[],
  marketplaceOrders: MarketplaceOrderListItemDto[] = [],
): Set<string> {
  const ids = collabIdsSet(collabData);
  marketplaceOrderIdsSet(marketplaceOrders).forEach((id) => ids.add(id));
  return ids;
}

/** Sanitize HTML from React Quill for safe display (bold, italic, underline, lists, indent). */
export function sanitizeMessageHtml(html: string): string {
  if (!html || typeof html !== "string") return "";
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["a", "p", "strong", "em", "u", "ol", "ul", "li", "br", "span"],
    ALLOWED_ATTR: ["class", "href"],
  });
}

/** Strip HTML tags for plain-text previews (e.g. inbox list). */
export function stripHtmlToPlainText(html: string): string {
  if (!html || typeof html !== "string") return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent?.trim() ?? "";
}

export type ConversationFileMediaKind = "image" | "video" | "other";

/** Infer image/video/other from a filename extension (conversation attachments). */
export function getMediaTypeFromFilename(filename: string): ConversationFileMediaKind {
  const ext = (filename.split(".").pop() ?? "").toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp", "heic"].includes(ext)) return "image";
  if (["mp4", "mov", "avi", "webm", "m4v"].includes(ext)) return "video";
  return "other";
}

/** Classifies a file message for inline preview using the stored filename only. */
export function getConversationFileMediaKind(message: Pick<Message, "body" | "type">): ConversationFileMediaKind {
  if (message.type !== "file") return "other";
  return getMediaTypeFromFilename(message.body);
}

/** Date pill labels between message groups in the thread (Today / Yesterday / short date). */
export function formatConversationThreadDateSeparator(
  timestamp: { seconds: number; nanoseconds: number } | undefined
): string {
  if (!timestamp) return "";
  const date = new Date(timestamp.seconds * 1000);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** One-line preview for the inbox row (text vs photo/video/file label). */
export function getConversationListPreviewText(message: Message): string {
  if (message.type === "file") {
    const kind = getConversationFileMediaKind(message);
    if (kind === "image") return "Photo";
    if (kind === "video") return "Video";
    return stripHtmlToPlainText(message.body) || "Attachment";
  }
  return stripHtmlToPlainText(message.body);
}

export const getLatestMessage = (messagesLog: Record<string, Message>): Message | null => {
    if (!messagesLog || Object.keys(messagesLog).length === 0) return null;
    const latestMessage = Object.values(messagesLog).sort((a, b) => 
      new Date(b?.time_sent?.toDate()).getTime() - new Date(a?.time_sent?.toDate()).getTime())[0];
    return latestMessage;
};

export const searchForMessagesWithoutSearchTerm = (
    conversations: Record<string, any>,
    collabData: Collab[],
    _influencerCampaignData: Record<string, Campaign>,
    marketplaceOrders: MarketplaceOrderListItemDto[] = [],
  ) => {
    const newFilteredConversations: { conversationId: string, message: Message }[] = [];
    const userConversationIds = inboxConversationIdsSet(collabData, marketplaceOrders);
    Object.keys(conversations).forEach(conversationId => {
      const conversation = conversations[conversationId];
      const messagesLog = conversation?.messages_log;
      const latestMessage = getLatestMessage(messagesLog);
      if (userConversationIds.has(conversationId)) {
        newFilteredConversations.push({ conversationId, message: latestMessage });
      }
    });
    return newFilteredConversations.sort((a, b) => {
      const timeA = a.message?.time_sent?.seconds || conversations[a.conversationId]?.time_created?.seconds || 0;
      const timeB = b.message?.time_sent?.seconds || conversations[b.conversationId]?.time_created?.seconds || 0;
      return timeB - timeA; // Sort by most recent time
    });
  };

/** Resolves the brand (client) account name for a conversation, if any. */
export function getConversationBrandName(
  conversationId: string,
  influencerCampaignData: Record<string, Campaign>,
  clientData: Record<string, Client>,
  marketplaceOrders: MarketplaceOrderListItemDto[] = [],
): string {
  const campaign = influencerCampaignData[conversationId];
  const campaignClient = campaign?.clientID ? clientData[campaign.clientID] : undefined;
  if (campaignClient?.accountName) return campaignClient.accountName;

  const order = marketplaceOrders.find((o) => o.id === conversationId);
  if (order?.brandName) return order.brandName;

  return '';
}

/** Returns the most recent message in the log whose body contains the (lowercased) term. */
function getLatestMatchingMessage(
  messagesLog: Record<string, Message> | undefined,
  lowerTerm: string
): Message | null {
  if (!messagesLog) return null;
  let best: Message | null = null;
  Object.values(messagesLog).forEach((message) => {
    const body = message?.body ?? "";
    if (!stripHtmlToPlainText(body).toLowerCase().includes(lowerTerm)) return;
    const bestSeconds = best?.time_sent?.seconds ?? -Infinity;
    const messageSeconds = message?.time_sent?.seconds ?? -Infinity;
    if (messageSeconds > bestSeconds) best = message;
  });
  return best;
}

export const searchForMessagesWithSearchTerm = (
  conversations: Record<string, any>,
  searchTerm: string,
  _addedMessages: Set<string>,
  collabData: Collab[],
  influencerCampaignData: Record<string, Campaign>,
  clientData: Record<string, Client> = {},
  marketplaceOrders: MarketplaceOrderListItemDto[] = [],
) => {
  const newFilteredConversations: { conversationId: string, message: Message }[] = [];
  const userConversationIds = inboxConversationIdsSet(collabData, marketplaceOrders);
  const term = searchTerm.toLowerCase().trim();
  if (!term) return newFilteredConversations;

  Object.keys(conversations).forEach(conversationId => {
    if (!userConversationIds.has(conversationId)) return;

    const conversation = conversations[conversationId];
    const messagesLog = conversation?.messages_log;

    const brandName = getConversationBrandName(
      conversationId,
      influencerCampaignData,
      clientData,
      marketplaceOrders,
    );
    const brandMatches = brandName.toLowerCase().includes(term);

    const latestMatchingMessage = getLatestMatchingMessage(messagesLog, term);

    if (!brandMatches && !latestMatchingMessage) return;

    // Prefer the most recent message that matched the body; fall back to latest message
    // for brand-only matches so the row still has something meaningful to render.
    const previewMessage = latestMatchingMessage ?? getLatestMessage(messagesLog);
    newFilteredConversations.push({ conversationId, message: previewMessage });
  });

  return newFilteredConversations.sort((a, b) => {
    const timeA = a.message?.time_sent?.seconds || conversations[a.conversationId]?.time_created?.seconds || 0;
    const timeB = b.message?.time_sent?.seconds || conversations[b.conversationId]?.time_created?.seconds || 0;
    return timeB - timeA;
  });
};

export function formatConversationDate(message: Message | null, conversation: any, atMs?: number): string {
  // atMs: the row previews an offer newer than the message, so it shows the offer's time.
  const dateToCheck = atMs ? new Date(atMs) : message?.time_sent?.toDate() || conversation?.time_created?.toDate();

  if (!dateToCheck) return "";

  const now = new Date();
  const isToday = dateToCheck.toDateString() === now.toDateString();
  const isYesterday = dateToCheck.toDateString() === new Date(now.setDate(now.getDate() - 1)).toDateString();
  const isThisWeek = dateToCheck > new Date(now.setDate(now.getDate() - 6));

  if (isToday) {
    return dateToCheck.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } else if (isYesterday) {
    return "Yesterday";
  } else if (isThisWeek) {
    return dateToCheck.toLocaleDateString(undefined, { weekday: 'long' });
  } else {
    return dateToCheck.toLocaleDateString();
  }
}

export const formatMessageTimestamp = (message: any, isSender: boolean, isLatestMessage: boolean): { formattedString: string } => {
  const timestamp = new Timestamp(
    ((isSender && isLatestMessage && message?.time_read?.seconds) || message?.time_sent?.seconds),
    ((isSender && isLatestMessage && message?.time_read?.nanoseconds) || message?.time_sent?.nanoseconds)
  );
  const dateToCheck = timestamp.toDate();

  const now = new Date();
  const isToday = dateToCheck.toDateString() === now.toDateString();
  const isYesterday = dateToCheck.toDateString() === new Date(now.setDate(now.getDate() - 1)).toDateString();
  const isThisWeek = dateToCheck > new Date(now.setDate(now.getDate() - 6));

  let dateString;
  if (isToday) {
    dateString = "Today";
  } else if (isYesterday) {
    dateString = "Yesterday";
  } else if (isThisWeek) {
    dateString = dateToCheck.toLocaleDateString(undefined, { weekday: 'long' });
  } else {
    dateString = dateToCheck.toLocaleDateString();
  }
  const timeString = dateToCheck.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const formattedString = `${dateString}, ${timeString}`;
  return { formattedString };
}; 

export const getUnreadCount = (messagesLog: Record<string, Message> | undefined): number => {
  // Collabs and orders without a conversation yet have no messages_log.
  return Object.values(messagesLog ?? {}).filter(message => 
    !message.time_read && message?.sender !== 'influencer' && !isSystemNote(message)
  ).length;
};

export type ThreadReadAction =
  | { kind: "none" }
  | { kind: "marketplace" }
  | { kind: "collab"; messageIds: string[] };

/**
 * What opening a thread should do to read state. Switched-account (superuser
 * impersonation) sessions never mark anything read — internal admins peeking
 * must not consume the creator's unread state or emit read receipts.
 */
export function getThreadReadAction(params: {
  messages: Record<string, Message> | null | undefined;
  isMarketplaceThread: boolean;
  isSwitchedAccount: boolean;
}): ThreadReadAction {
  const { messages, isMarketplaceThread, isSwitchedAccount } = params;
  if (!messages || isSwitchedAccount) return { kind: "none" };
  if (isMarketplaceThread) return { kind: "marketplace" };
  const messageIds = Object.entries(messages)
    .filter(([, message]) => message?.sender !== "influencer" && !message?.time_read && !isSystemNote(message))
    .map(([key]) => key);
  return { kind: "collab", messageIds };
}

export const getLatestReadMessageId = (messagesLog: Record<string, Message>): string | null => {
  if (messagesLog == null) {
    return null;
  }

  const entries = Object.entries(messagesLog)
    .filter(([_, message]) => message?.time_read && message?.sender === 'influencer')
    .sort(([, a], [, b]) => b?.time_sent?.seconds - a?.time_sent?.seconds);

  if (entries.length === 0) return null;

  const [key] = entries[0];
  return key;
};

/**
 * Any timestamp shape as comparable milliseconds, 0 when missing. Nanoseconds are kept for
 * `{seconds, nanoseconds}` stamps (same-second ties matter when ordering messages and cards).
 */
export const millisOf = (timestamp?: TimestampLike | null): number => {
  if (!timestamp) return 0;
  const { seconds, nanoseconds } = timestamp as { seconds?: unknown; nanoseconds?: unknown };
  if (typeof seconds === "number") {
    return seconds * 1000 + (typeof nanoseconds === "number" ? nanoseconds : 0) / 1e6;
  }
  return toDate(timestamp)?.getTime() ?? 0;
};

/**
 * Creator-archived campaign conversations, Gmail-style — the twin of the dashboard's
 * `resolveArchivedConversations`. `archivedByCreatorAt` hides the thread only until the brand writes
 * after that moment; system notes and the creator's own messages don't bring it back. Nothing writes
 * on the message path: a newer brand message simply out-dates the stamp.
 */
export const resolveCreatorArchivedConversations = (conversations: Record<string, any>): Set<string> => {
  const archived = new Set<string>();
  Object.entries(conversations ?? {}).forEach(([id, conv]) => {
    const archivedAt = millisOf(conv?.archivedByCreatorAt);
    if (!archivedAt) return;
    const messages = Object.values((conv?.messages_log ?? {}) as Record<string, Message>);
    const brandRepliedSince = messages.some(
      (m) => !!m && m.sender === "client" && !isSystemNote(m) && millisOf(m.time_sent) > archivedAt,
    );
    if (!brandRepliedSince) archived.add(id);
  });
  return archived;
};

export interface InboxSections<T> {
  potential: T[];
  others: T[];
  archived: T[];
}

/**
 * Splits the (already sorted) inbox rows into sections. Potential = the collab is still
 * pre-acceptance (derived from status, so it moves on acceptance with no write). Archived wins over
 * Potential. Marketplace orders are never archived or potential — they stay in the normal list.
 */
export function groupInboxConversations<T extends { conversationId: string }>(
  rows: T[],
  collabData: Collab[],
  archivedIds: Set<string>,
  marketplaceOrders: MarketplaceOrderListItemDto[] = [],
): InboxSections<T> {
  const orderIds = marketplaceOrderIdsSet(marketplaceOrders);
  const collabById = new Map(collabData.map((c) => [c.id, c]));
  const sections: InboxSections<T> = { potential: [], others: [], archived: [] };
  rows.forEach((row) => {
    const id = row.conversationId;
    if (orderIds.has(id)) {
      sections.others.push(row);
      return;
    }
    if (archivedIds.has(id)) {
      sections.archived.push(row);
      return;
    }
    const collab = collabById.get(id);
    (collab && isPreAcceptance(collab) ? sections.potential : sections.others).push(row);
  });
  return sections;
}

/**
 * Content is in and waiting on the brand: the same state collabUtils maps to 'pendingVerification'.
 * waitingForVerification alone is true for a bare pendingSubmission collab (nothing to wait on),
 * so also require at least one active submitted unit or deliverable link.
 */
export function isContentSubmitted(collab: Collab | undefined): boolean {
  if (!collab || collab.status !== 'pendingSubmission') return false;
  const hasSubmission =
    activeUnits(collab.content).length > 0 || activeLinks(collab.deliverableLinks).length > 0;
  return hasSubmission && waitingForVerification(collab);
}
